import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { FileText } from 'lucide-react';
import '../styles/enterprise.css';
import '../styles/responsive/pages/Reports.css';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import FilterTabs from '../components/common/FilterTabs';
import StatusBadge from '../components/common/StatusBadge';
import { TableSkeleton } from '../components/common/Skeleton';
import {
  useGetMerchantDossierQuery,
  useGetMerchantReconciliationQuery,
} from '../store/api/adminOpsApi';
import { useGetAnalyticsOverviewQuery } from '../store/api/analyticsApi';
import { formatUSD, formatCount, truncateHash } from '../utils/formatAmount';

const fmtBucket = (b) => {
  if (!b) return '';
  const parts = b.split('-');
  return `${parts[1]}/${parts[2]}`;
};
const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const TABS = [
  { key: 'dossier', label: 'Merchant Dossier' },
  { key: 'register', label: 'Payout Register' },
  { key: 'platform', label: 'Platform Volume' },
];

const EmptyChart = ({ height = 260, label = 'No data for this range' }) => (
  <div className="reports-empty-chart" style={{ height }}>
    {label}
  </div>
);

// ---- Merchant dossier + reconciliation -----------------------------------
const DossierTab = ({ merchantId }) => {
  const { data: dossier, isLoading: dLoading } = useGetMerchantDossierQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });
  const { data: recon, isLoading: rLoading } = useGetMerchantReconciliationQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });

  if (!merchantId) {
    return (
      <div className="empty-state">
        <FileText size={48} />
        <p>No merchant selected</p>
        <span>Enter a merchant id above to load their dossier</span>
      </div>
    );
  }

  if (dLoading) return <TableSkeleton rows={5} cols={4} />;

  const m = dossier?.merchant || {};
  const totals = dossier?.totals || {};
  const reconRows = recon?.chains || recon?.rows || [];

  return (
    <>
      <KpiStrip>
        <KpiCard label="LIFETIME VOLUME (USD)" value={`$${formatUSD(totals.volumeUsd || 0)}`} sub={`${formatCount(totals.txCount || 0)} tx`} />
        <KpiCard label="LIFETIME PAID OUT (USD)" value={`$${formatUSD(totals.paidOutUsd || 0)}`} sub={`${formatCount(totals.payoutCount || 0)} payouts`} />
        <KpiCard label="AVAILABLE BALANCE (USD)" value={`$${formatUSD(totals.availableUsd || 0)}`} />
        <KpiCard label="OUTSTANDING (USD)" value={`$${formatUSD(totals.outstandingUsd || 0)}`} />
      </KpiStrip>

      <div className="reports-panel">
        <div className="reports-panel__title">Merchant</div>
        <div className="reports-kv">
          <div><span>Name</span><strong>{m.name || '—'}</strong></div>
          <div><span>Email</span><strong>{m.email || '—'}</strong></div>
          <div><span>Plan</span><strong style={{ textTransform: 'capitalize' }}>{m.planKey || '—'}</strong></div>
          <div>
            <span>Status</span>
            <StatusBadge status={m.status === 'suspended' ? 'failed' : 'active'} label={m.status === 'suspended' ? 'Suspended' : 'Active'} dot />
          </div>
        </div>
      </div>

      <div className="reports-panel">
        <div className="reports-panel__title">Reconciliation — ledger vs on-chain</div>
        {rLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : reconRows.length === 0 ? (
          <EmptyChart height={120} label="No reconciliation data" />
        ) : (
          <div className="pro-table-wrap">
            <table className="pro-table">
              <thead>
                <tr>
                  <th>CHAIN</th>
                  <th className="amount-cell">LEDGER (USD)</th>
                  <th className="amount-cell">ON-CHAIN (USD)</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {reconRows.map((r) => {
                  const diff = Number(r.onChainUsd ?? r.onChain ?? 0) - Number(r.ledgerUsd ?? r.ledger ?? 0);
                  const matched = Math.abs(diff) < 0.01;
                  return (
                    <tr key={r.networkId || r.chain}>
                      <td data-label="CHAIN"><span className="cell-2__main">{r.chain || r.name}</span></td>
                      <td className="amount-cell" data-label="LEDGER"><span className="amount-cell__main mono">${formatUSD(r.ledgerUsd ?? r.ledger ?? 0)}</span></td>
                      <td className="amount-cell" data-label="ON-CHAIN"><span className="amount-cell__main mono">${formatUSD(r.onChainUsd ?? r.onChain ?? 0)}</span></td>
                      <td data-label="STATUS">
                        <StatusBadge status={matched ? 'active' : 'failed'} label={matched ? 'Matched' : 'Mismatch'} dot />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

// ---- Payout register (from the dossier payload) --------------------------
const RegisterTab = ({ merchantId }) => {
  const { data: dossier, isLoading } = useGetMerchantDossierQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: true,
  });

  if (!merchantId) {
    return (
      <div className="empty-state">
        <FileText size={48} />
        <p>No merchant selected</p>
        <span>Enter a merchant id above to load the payout register</span>
      </div>
    );
  }
  if (isLoading) return <TableSkeleton rows={6} cols={5} />;

  const payouts = dossier?.payouts || [];
  if (payouts.length === 0) {
    return (
      <div className="empty-state">
        <FileText size={48} />
        <p>No payouts on record</p>
      </div>
    );
  }

  return (
    <div className="pro-table-wrap">
      <table className="pro-table">
        <thead>
          <tr>
            <th>REQUEST</th>
            <th className="amount-cell">AMOUNT (USD)</th>
            <th>DESTINATION</th>
            <th>STATUS</th>
            <th>DATE</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((p) => (
            <tr key={p.id || p.requestId}>
              <td data-label="REQUEST"><span className="mono">{p.requestId || p.id}</span></td>
              <td className="amount-cell" data-label="AMOUNT"><span className="amount-cell__main mono">${formatUSD(p.amount || 0)}</span></td>
              <td data-label="DESTINATION"><span className="mono">{p.destinationAddress ? truncateHash(p.destinationAddress, 6, 6) : '—'}</span></td>
              <td data-label="STATUS"><StatusBadge status={p.status} dot /></td>
              <td data-label="DATE"><span className="cell-2__sub">{fmtDate(p.createdAt || p.created_at)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---- Platform volume dashboards (reuse analytics aggregation) ------------
const PlatformTab = () => {
  const { mode } = useTheme();
  const isMobile = useIsMobile();
  const isTestnet = mode === 'test' ? 'true' : 'false';
  const [range, setRange] = useState('30d');

  const { data: overview, isLoading } = useGetAnalyticsOverviewQuery({ range, isTestnet });
  const totals = overview?.totals || { revenueUsd: 0, txCount: 0, succeeded: 0, successRate: null };
  const series = overview?.series || [];
  const hasSeries = series.some((p) => p.txCount > 0);

  const ranges = [
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: '12m', label: '12M' },
  ];

  return (
    <>
      <div className="reports-range" role="group" aria-label="Date range">
        {ranges.map((r) => (
          <button
            key={r.key}
            className={`ent-btn ${range === r.key ? 'ent-btn-primary' : 'ent-btn-secondary'}`}
            onClick={() => setRange(r.key)}
            aria-pressed={range === r.key}
          >
            {r.label}
          </button>
        ))}
      </div>

      <KpiStrip>
        <KpiCard label="PLATFORM REVENUE" value={`$${formatUSD(totals.revenueUsd)}`} sub={`${range} settled`} />
        <KpiCard label="TRANSACTIONS" value={formatCount(totals.txCount)} />
        <KpiCard label="SUCCESS RATE" value={totals.successRate != null ? `${totals.successRate}%` : '—'} />
      </KpiStrip>

      {isLoading ? (
        <TableSkeleton rows={4} cols={2} />
      ) : (
        <div className={`reports-charts ${isMobile ? 'mobile' : ''}`}>
          <div className="reports-panel">
            <div className="reports-panel__title">Revenue (USD)</div>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => `$${formatUSD(v)}`} labelFormatter={fmtBucket} />
                  <Line type="monotone" dataKey="revenueUsd" name="Revenue" stroke="#635bff" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </div>
          <div className="reports-panel">
            <div className="reports-panel__title">Transaction volume</div>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip labelFormatter={fmtBucket} />
                  <Bar dataKey="txCount" name="Transactions" fill="#635bff" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const Reports = () => {
  const { mode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const merchantId = searchParams.get('merchantId') || '';
  const [tab, setTab] = useState('dossier');
  const [merchantInput, setMerchantInput] = useState(merchantId);

  const applyMerchant = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (merchantInput) next.set('merchantId', merchantInput);
    else next.delete('merchantId');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="ent ent-page reports-page">
      <PageHeaderPro title="Reports" mode={mode === 'test' ? 'test' : 'live'} />

      <FilterTabs tabs={TABS} active={tab} onChange={setTab} />

      {tab !== 'platform' && (
        <form className="reports-merchant-bar" onSubmit={applyMerchant}>
          <label>
            <span>Merchant ID</span>
            <input
              type="text"
              value={merchantInput}
              onChange={(e) => setMerchantInput(e.target.value)}
              placeholder="Enter merchant id"
            />
          </label>
          <button type="submit" className="ent-btn ent-btn-secondary">
            Load
          </button>
        </form>
      )}

      <div className="reports-body">
        {tab === 'dossier' && <DossierTab merchantId={merchantId} />}
        {tab === 'register' && <RegisterTab merchantId={merchantId} />}
        {tab === 'platform' && <PlatformTab />}
      </div>
    </div>
  );
};

export default Reports;
